const MONTHS = {
  jan: '01',
  feb: '02',
  mar: '03',
  apr: '04',
  may: '05',
  jun: '06',
  jul: '07',
  aug: '08',
  sep: '09',
  oct: '10',
  nov: '11',
  dec: '12',
};

const ROUTE_SEPARATOR_REGEX = /\s*(?:→|➜|->|=>|から|\bto\b)\s*/i;

const AREA_ALIASES = {
  narita_airport: ['成田', 'narita', 'nrt'],
  haneda_airport: ['羽田', 'haneda', 'hnd'],
  tokyo: ['東京', 'tokyo', '銀座', 'ginza', '新宿', 'shinjuku', '渋谷', 'shibuya', '港区', '千代田', '江東', 'koto', 'kiyosumi', 'ホテル', 'hotel', '浅草', '浅草寺', '上野', '秋葉原', '仲見世'],
  yokohama_port: ['横浜', 'yokohama'],
  mt_fuji: ['富士山', 'mt fuji', 'fuji', '河口湖', '山中湖', '忠霊塔', '忠灵塔', '新倉山', '新仓山', '新倉山浅間公園', '西湖', '大石公園'],
  hakone: ['箱根', 'hakone', '箱根湯本', '強羅', '強羅花壇', '早雲山', '大涌谷', '芦ノ湖', '箱根ロープウェイ', '箱根海賊船', '箱根彫刻の森'],
  odawara: ['小田原', '小田原駅', 'odawara'],
  nikko: ['日光', 'nikko', '東照宮', '輪王寺', '中禅寺湖', 'toshogu'],
  kawagoe: ['川越', 'kawagoe'],
  shizuoka: ['静岡', '静冈', 'shizuoka', '清水港', '清水', 'shimizu', 'shimizu port'],
  kanazawa: ['金沢', '金泽', 'kanazawa'],
  osaka: ['大阪', 'osaka', '関西', 'kansai', 'kix'],
  kyoto: ['京都', 'kyoto'],
  okinawa: ['沖縄', 'okinawa', 'naha', '那覇'],
};

const AREA_DISTANCE_PRIORITY = {
  tokyo: 1,
  haneda_airport: 1,
  yokohama_port: 2,
  narita_airport: 2,
  odawara: 3,
  shizuoka: 3,
  mt_fuji: 4,
  hakone: 4,
  nikko: 4,
  kawagoe: 4,
  osaka: 1,
  kyoto: 2,
  nara: 2,
  kobe: 2,
  kanazawa: 1,
  okinawa: 1,
};

const cleanValue = (value) => (value || '').replace(/^[：:\s・-]+/, '').trim();

const addRecognized = (recognized, label) => {
  if (!recognized.includes(label)) {
    recognized.push(label);
  }
};

const matchLineValue = (text, labels) => {
  const escaped = labels.map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const regex = new RegExp(`(?:^|\\n)\\s*(?:[・■•]\\s*)?(?:${escaped})\\s*[：:\\-]?\\s*([^\\n\\r]+)`, 'i');
  const match = text.match(regex);
  if (match) return cleanValue(match[1]);

  const bracketRegex = new RegExp(`[【\\[]\\s*(?:${escaped})\\s*[】\\]]\\s*\\n\\s*([^\\n\\r]+)`, 'i');
  const bracketMatch = text.match(bracketRegex);
  if (bracketMatch) return cleanValue(bracketMatch[1]);

  const nextLineRegex = new RegExp(`(?:^|\\n)\\s*(?:[■•]\\s*)?(?:${escaped})\\s*$\\s*\\n\\s*([^\\n\\r]+)`, 'im');
  const nextLineMatch = text.match(nextLineRegex);
  return nextLineMatch ? cleanValue(nextLineMatch[1]) : '';
};

const areaText = (area) => [
  area.code,
  area.name_zh,
  area.name_ja,
  area.name_en,
  ...(area.aliases || []),
  area.region,
  area.rule_group,
].filter(Boolean).join(' ').toLowerCase();

const GENERIC_AREA_ALIASES = new Set(['hotel', 'ホテル']);

export const inferAreaCode = (value, areas = []) => {
  const text = (value || '').toLowerCase();
  if (!text) return '';

  for (const [code, aliases] of Object.entries(AREA_ALIASES)) {
    if (aliases.some((alias) => !GENERIC_AREA_ALIASES.has(alias.toLowerCase()) && text.includes(alias.toLowerCase()))) {
      return code;
    }
  }

  for (const [code, aliases] of Object.entries(AREA_ALIASES)) {
    if (aliases.some((alias) => GENERIC_AREA_ALIASES.has(alias.toLowerCase()) && text.includes(alias.toLowerCase()))) {
      return code;
    }
  }

  const directArea = areas.find((area) => areaText(area).includes(text) || text.includes(areaText(area)));
  if (directArea) return directArea.code;

  const fuzzyArea = areas.find((area) => {
    const labels = [area.name_zh, area.name_ja, area.name_en, area.code].filter(Boolean);
    return labels.some((label) => text.includes(String(label).toLowerCase()));
  });
  return fuzzyArea?.code || '';
};

const isValidMonthDay = (year, month, day) => {
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

const parseDate = (text, options = {}) => {
  const jpDate = text.match(/(\d{4})年\s*(\d{1,2})月\s*(\d{1,2})日/);
  if (jpDate) {
    return `${jpDate[1]}-${jpDate[2].padStart(2, '0')}-${jpDate[3].padStart(2, '0')}`;
  }

  const monthDay = text.match(/(?:^|[^\d])(\d{1,2})月\s*(\d{1,2})日/);
  if (monthDay) {
    const year = new Date().getFullYear();
    const month = parseInt(monthDay[1], 10);
    const day = parseInt(monthDay[2], 10);
    if (isValidMonthDay(year, month, day)) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }

  const slashMonth = text.match(/(\d{1,2})\/([A-Za-z]{3})\/(\d{4})/);
  if (slashMonth) {
    const month = MONTHS[slashMonth[2].toLowerCase()];
    if (month) return `${slashMonth[3]}-${month}-${slashMonth[1].padStart(2, '0')}`;
  }

  const dayMonth = text.match(/(?:\b(?:mon|tue|wed|thu|fri|sat|sun)(?:day)?\b,\s*)?(\d{1,2})\s+([A-Za-z]{3,9})\b/i);
  if (dayMonth) {
    const month = MONTHS[dayMonth[2].slice(0, 3).toLowerCase()];
    if (month) {
      return `${new Date().getFullYear()}-${month}-${dayMonth[1].padStart(2, '0')}`;
    }
  }

  const standard = text.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (standard) {
    return `${standard[1]}-${standard[2].padStart(2, '0')}-${standard[3].padStart(2, '0')}`;
  }

  const dayOnly = text.match(/(?:^|[^\d])(\d{1,2})日(?:[^\d]|$)/);
  if (dayOnly) {
    const now = new Date();
    const day = parseInt(dayOnly[1], 10);
    const defaultMonth = parseInt(options.defaultMonth, 10);
    let year = now.getFullYear();
    let month = Number.isFinite(defaultMonth) && defaultMonth >= 1 && defaultMonth <= 12
      ? defaultMonth
      : now.getMonth() + 1;
    const hasDefaultMonth = Number.isFinite(defaultMonth) && defaultMonth >= 1 && defaultMonth <= 12;

    if (!isValidMonthDay(year, month, day)) {
      month += 1;
      if (month > 12) {
        month = 1;
        year += 1;
      }
    } else if (!hasDefaultMonth) {
      const inferredDate = new Date(year, month - 1, day);
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (inferredDate < today) {
        month += 1;
        if (month > 12) {
          month = 1;
          year += 1;
        }
      }
    }

    if (isValidMonthDay(year, month, day)) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }

  return '';
};

const VEHICLE_TYPE_PATTERNS = [
  {
    type: 'hiace_14_seats',
    regex: /14\s*[- ]?\s*seats?\s*hiace|14座ハイエース|14座海[狮獅]/i,
  },
  {
    type: 'coaster',
    regex: /coaster|コースター|考斯特/i,
  },
  {
    type: 'bus',
    regex: /\bbus\b|バス|巴士/i,
  },
  {
    type: 'hiace',
    regex: /premium\s*min[ij]\s*van|premium\s*minivan|min[ij]\s*van|minivan/i,
  },
  {
    type: 'alphard',
    regex: /standard\s*car|stand\s*car|premium\s*suv|\bsuv\b/i,
  },
  {
    type: 'premium_hiace',
    regex: /(?:premium|luxury|プレミアム|グランド|豪華)[\s_-]*(?:hiace|ハイエース|海狮|海獅)|(?:hiace|ハイエース|海狮|海獅)[\s_-]*(?:premium|luxury|プレミアム|グランド|豪華)|プレミアムハイエース|豪華海[狮獅]/i,
  },
  {
    type: 'alphard',
    regex: /alphard|アルファード|埃尔法|埃爾法/i,
  },
  {
    type: 'hiace',
    regex: /hiace|ハイエース|海狮|海獅/i,
  },
];

const parseVehicleType = (text) => (
  VEHICLE_TYPE_PATTERNS.find(({ regex }) => regex.test(text || ''))?.type || ''
);

const parseVehicleCount = (value, type) => {
  const text = value || '';
  const typePattern = {
    alphard: '(?:toyota\\s*)?alphard|アルファード|埃尔法',
    hiace: '(?:toyota\\s*)?hiace|ハイエース|海狮|海獅|(?:premium\\s*)?min[ij]\\s*van|(?:premium\\s*)?minivan',
    premium_hiace: '(?:premium|luxury|プレミアム|グランド|豪華).*?(?:hiace|ハイエース|海狮|海獅)',
    hiace_14_seats: '(?:14\\s*[- ]?\\s*seats?\\s*hiace|14座ハイエース|14座海[狮獅])',
    coaster: 'coaster|コースター|考斯特',
    bus: '\\bbus\\b|バス|巴士',
  }[type];
  if (!typePattern) return 1;

  const leadingCount = text.match(new RegExp(`(?:^|\\D)(\\d+)\\s*(?:x\\s*)?(?:${typePattern})`, 'i'));
  const trailingCount = text.match(new RegExp(`(?:${typePattern})[\\s\\S]{0,20}?(\\d+)\\s*(?:台|cars?|vehicles?|units?)`, 'i'));
  const genericCount = text.match(/(?:^|\D)(\d+)\s*(?:台|cars?|vehicles?|units?)/i);
  const count = parseInt((leadingCount || trailingCount || genericCount || [])[1] || '1', 10);
  return Number.isFinite(count) && count > 0 ? Math.min(count, 10) : 1;
};

const parseVehicleRequests = (text) => {
  const value = text || '';
  const segments = value
    .split(/\s*(?:\+|＋|&|\band\b|;)\s*/i)
    .map((segment) => segment.trim())
    .filter(Boolean);
  const vehicleSegments = segments.length > 0 ? segments : [value];

  return vehicleSegments.flatMap((segment) => {
    const type = parseVehicleType(segment);
    if (!type) return [];
    return Array(parseVehicleCount(segment, type)).fill(type);
  });
};

const parseRouteParts = (text) => {
  const routeLine =
    matchLineValue(text, ['Transfer', '送迎', '送迎区間']) ||
    matchLineValue(text, ['行程', 'ルート', 'route']) ||
    text.split('\n').find((line) => /→|➜|->|=>|から|\bto\b/i.test(line) && !/^\s*(?:remarks?|note|备注|備考)\s*[：:-]/i.test(line)) ||
    '';

  const routeMatch = routeLine.match(/(.+?)(?:→|➜|->|=>|から|\bto\b)(.+)/i);
  if (!routeMatch) {
    return { pickup: '', dropoff: '', parts: [] };
  }
  const routeParts = routeLine
    .split(ROUTE_SEPARATOR_REGEX)
    .map((part) => cleanValue(part))
    .filter(Boolean);

  return {
    pickup: routeParts[0] || cleanValue(routeMatch[1]),
    dropoff: routeParts[routeParts.length - 1] || cleanValue(routeMatch[2]),
    parts: routeParts,
  };
};

const lineStartsWithAnyLabel = (line, labels) => {
  const escaped = labels.map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  return new RegExp(`^\\s*(?:[・■•]\\s*)?(?:${escaped})\\s*[：:\\-]`, 'i').test(line || '');
};

const appendDetailParts = (base, parts) => {
  const values = [base, ...parts]
    .map((part) => cleanValue(part))
    .filter(Boolean);
  return values.reduce((acc, value) => {
    if (acc.some((item) => item === value || item.includes(value))) return acc;
    return [...acc, value];
  }, []).join('\n');
};

const inferAddressDetailTarget = (text) => {
  const pickupLabels = ['Pick-Up Location', 'Pickup Location', 'Pick Up Location', '出発地', '乗車地', '乗車場所', '上車地', '上车地点', '上車地点', '上车地', '出发地点'];
  const dropoffLabels = ['Drop-Off Location', 'Dropoff Location', 'Drop Off Location', '目的地', '降車地', '下車地', '下车地点', '下車地点', '下车地', '到达地点'];
  let lastStop = '';
  let addressTarget = '';

  (text || '').split(/\r?\n/).forEach((line) => {
    if (lineStartsWithAnyLabel(line, pickupLabels)) {
      lastStop = 'pickup';
      return;
    }
    if (lineStartsWithAnyLabel(line, dropoffLabels)) {
      lastStop = 'dropoff';
      return;
    }
    if (lineStartsWithAnyLabel(line, ['住所', 'Address', 'Google Maps', 'Google Map', 'Map'])) {
      addressTarget = addressTarget || lastStop;
    }
  });

  return addressTarget;
};

const inferFarthestRouteArea = (routeParts, pickupArea, finalArea, areas) => {
  const hits = [];
  routeParts.forEach((part) => {
    const area = inferAreaCode(part, areas);
    if (area && !hits.includes(area)) {
      hits.push(area);
    }
  });
  const candidates = hits.filter((area) => area !== pickupArea);
  if (candidates.length === 0 && finalArea) {
    candidates.push(finalArea);
  }
  if (candidates.length === 0) return '';
  return candidates.reduce((best, area) => (
    (AREA_DISTANCE_PRIORITY[area] || 0) > (AREA_DISTANCE_PRIORITY[best] || 0) ? area : best
  ), candidates[0]);
};

const parseTimeValue = (value) => {
  const match = (value || '').match(/([01]?\d|2[0-3])[:：時]\s*([0-5]\d)/);
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : '';
};

const calculateHoursFromTimes = (start, end) => {
  if (!start || !end) return null;
  const [startHour, startMinute] = start.split(':').map(Number);
  const [endHour, endMinute] = end.split(':').map(Number);
  let minutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
  if (minutes <= 0) minutes += 24 * 60;
  return Math.ceil((minutes / 60) * 2) / 2;
};

const ORDER_BLOCK_START = /^\s*[A-Z0-9]{2,8}\s*[-_]\s*\d{1,6}(?:\b|[\s　・:：、,⭕️])/i;
const ENGLISH_ORDER_BLOCK_START = /^\s*Order\s+\d+(?:[-_]\d+)?\s*$/i;
const JAPANESE_RESERVATION_BLOCK_START = /^\s*【\s*予約情報\s*(?:\d+|[①②③④⑤⑥⑦⑧⑨⑩]+)\s*】/i;
const TRANSFER_SECTION_START = /^\s*(?:arrival|departure)\s+transfer\b/i;
const CHINESE_ORDER_BLOCK_START = /^\s*第\s*(?:\d+|[一二三四五六七八九十]+)\s*(?:单|單|笔|筆|件)(?:\s|$|[：:])/i;
const DATE_ORDER_BLOCK_START = /^\s*(?:[・■•]\s*)?(?:出発日|日付|日期|Date)\s*[：:\-]/i;
const ORDER_LIKE_CONTENT = /(?:^|\n)\s*(?:[・■•]\s*)?(?:日付|出発日|日期|时间|時間|上车地点|上車地点|下车地点|下車地点|服务内容|服務內容|价格|價格|date|flight|transfer|vehicle|passengers|luggage|車両|车型|車種|行程|乗客数|お荷物|客户名|客戶名|お客様コード|お客様名|サービスタイプ|乗車場所)\s*[：:\-]/i;
const DATE_SECTION_CONTENT = /(?:^|\n)\s*(?:[・■•]\s*)?(?:出発日|日付|日期|Date)\s*[：:\-]/i;

const hasOrderLikeContent = (text) => ORDER_LIKE_CONTENT.test(text) || text.split(/\r?\n/).some((line) => ORDER_BLOCK_START.test(line) || ENGLISH_ORDER_BLOCK_START.test(line) || JAPANESE_RESERVATION_BLOCK_START.test(line) || CHINESE_ORDER_BLOCK_START.test(line));

const inferDefaultMonthFromBatch = (text) => {
  const monthMatch = (text || '').match(/(?:^|[^\d])(\d{1,2})月\s*\d{1,2}日/);
  const month = parseInt(monthMatch?.[1] || '', 10);
  return Number.isFinite(month) && month >= 1 && month <= 12 ? month : null;
};

export function splitAdminOrderBatchText(rawText) {
  const lines = (rawText || '').split(/\r?\n/);
  const blocks = [];
  let current = [];
  let commonPrefix = '';

  lines.forEach((line) => {
    const startsOrderBlock = ORDER_BLOCK_START.test(line);
    const startsEnglishOrderBlock = ENGLISH_ORDER_BLOCK_START.test(line);
    const startsJapaneseReservationBlock = JAPANESE_RESERVATION_BLOCK_START.test(line);
    const startsTransferSection = TRANSFER_SECTION_START.test(line);
    const startsChineseOrderBlock = CHINESE_ORDER_BLOCK_START.test(line);
    const startsDateOrderBlock = DATE_ORDER_BLOCK_START.test(line);
    if ((startsOrderBlock || startsEnglishOrderBlock || startsJapaneseReservationBlock || startsTransferSection || startsChineseOrderBlock || startsDateOrderBlock) && current.some((item) => item.trim())) {
      const currentText = current.join('\n').trim();
      const currentFirstLine = currentText.split('\n')[0] || '';
      const currentStartsKnownBlock = ORDER_BLOCK_START.test(currentFirstLine) ||
        ENGLISH_ORDER_BLOCK_START.test(currentFirstLine) ||
        JAPANESE_RESERVATION_BLOCK_START.test(currentFirstLine) ||
        TRANSFER_SECTION_START.test(currentFirstLine) ||
        CHINESE_ORDER_BLOCK_START.test(currentFirstLine) ||
        DATE_ORDER_BLOCK_START.test(currentFirstLine);
      const shouldSplitDateSection = startsDateOrderBlock && (
        DATE_SECTION_CONTENT.test(currentText) ||
        (!currentStartsKnownBlock && !/→|➜|->|=>|から|\bto\b/i.test(currentText))
      );
      if (startsDateOrderBlock && !shouldSplitDateSection) {
        current.push(line);
        return;
      }
      if ((startsChineseOrderBlock || startsDateOrderBlock) && !currentStartsKnownBlock && !DATE_SECTION_CONTENT.test(currentText) && !commonPrefix) {
        commonPrefix = currentText;
      } else if (hasOrderLikeContent(currentText)) {
        blocks.push(commonPrefix && (startsChineseOrderBlock || startsDateOrderBlock) ? `${commonPrefix}\n${currentText}` : currentText);
      }
      current = [line];
      return;
    }
    current.push(line);
  });

  if (current.some((item) => item.trim())) {
    const currentText = current.join('\n').trim();
    if (hasOrderLikeContent(currentText) || blocks.length === 0) {
      const startsReusableBlock = CHINESE_ORDER_BLOCK_START.test(currentText.split('\n')[0] || '') ||
        ENGLISH_ORDER_BLOCK_START.test(currentText.split('\n')[0] || '') ||
        JAPANESE_RESERVATION_BLOCK_START.test(currentText.split('\n')[0] || '') ||
        DATE_ORDER_BLOCK_START.test(currentText.split('\n')[0] || '');
      blocks.push(commonPrefix && startsReusableBlock ? `${commonPrefix}\n${currentText}` : currentText);
    }
  }

  return blocks.filter(Boolean);
}

export function parseAdminOrderText(rawText, areas = [], options = {}) {
  const text = rawText || '';
  const parsed = {};
  const recognized = [];
  const firstLine = text.split('\n')[0]?.trim() || '';

  const firstLineOrderMatch = firstLine.match(/^\s*([A-Z0-9]{2,8})\s*[-_]\s*(\d{1,6})/i);
  const labeledOrderMatch = text.match(/(?:Order\s*#|Booking\s*Code|Customer\s*Code|Booking\s*ID|Reservation\s*#|订单号|客户代码|客户编号|お客様コード|顧客コード|注文番号|予約番号|受付番号)[：:\s#-]*([A-Z0-9-]+)/i);
  if (firstLineOrderMatch) {
    parsed.customer_code = `${firstLineOrderMatch[1]}-${firstLineOrderMatch[2]}`.toUpperCase();
    addRecognized(recognized, '订单号/客户代码');
  } else if (labeledOrderMatch) {
    parsed.customer_code = labeledOrderMatch[1].replace('_', '-').toUpperCase();
    addRecognized(recognized, '订单号/客户代码');
  }

  const passengerValue = matchLineValue(text, ['乗客数', 'ご利用人数', '参加人数', '人数', '乘客数', '乘客人数', 'No. of Passengers', 'Passengers']);
  const adultChildPassengerMatch = passengerValue.match(/(\d+)\s*adults?.*?(\d+)\s*children?/i);
  const passengerMatch = passengerValue.match(/(\d+)/) || text.match(/[（(]?(\d+)名(?:様)?[)）]?/);
  if (adultChildPassengerMatch) {
    parsed.passenger_count = parseInt(adultChildPassengerMatch[1], 10) + parseInt(adultChildPassengerMatch[2], 10);
    addRecognized(recognized, `乗客人数(${parsed.passenger_count}名)`);
  } else if (passengerMatch) {
    parsed.passenger_count = parseInt(passengerMatch[1]);
    if (/ガイド|guide|导游|導遊/i.test(passengerValue)) {
      parsed.passenger_count += 1;
    }
    addRecognized(recognized, `乗客人数(${parsed.passenger_count}名)`);
  }

  const parsedDate = parseDate(matchLineValue(text, ['日付', '出発日', '日期', 'Service Date', 'Date']) || text, options);
  if (parsedDate) {
    parsed.departure_date = parsedDate;
    addRecognized(recognized, '出発日');
  }

  const timeValue = matchLineValue(text, ['出発時間', '出发时间', '时间', 'Pick-Up Time', 'Pickup Time', 'Arrival Time', '時間']);
  const parsedPickupTime = parseTimeValue(timeValue || text);
  if (parsedPickupTime) {
    parsed.pickup_time = parsedPickupTime;
    addRecognized(recognized, '出発時間');
  }

  const nameValue = matchLineValue(text, [
    'ご利用者名',
    'ご利用者様',
    'お客様名',
    '氏名',
    'お名前',
    'Lead Passenger',
    'Passenger Name',
    '客户名',
    '客戶名',
    '客户姓名',
    '客戶姓名',
    'Name',
  ]);
  const nameMatch = nameValue || text.match(/(?:Mr\.|Ms\.|Mrs\.)\s+([A-Za-z][A-Za-z\s]+)/)?.[0] || '';
  if (nameMatch) {
    parsed.customer_name = cleanValue(nameMatch)
      .replace(/^(Mr\.|Ms\.|Mrs\.)\s*/i, '')
      .replace(/\s*(様|さま|さん|殿)$/g, '')
      .trim();
    addRecognized(recognized, 'お客様名');
  }

  const companyValue = matchLineValue(text, ['会社名', '公司名', 'Company']);
  if (companyValue && !parsed.customer_name) {
    parsed.customer_name = companyValue;
    addRecognized(recognized, '会社名');
  }

  const phoneValue = matchLineValue(text, ['phone', '電話番号', '電話', '携帯', 'TEL', 'Mobile', '連絡先']);
  const phoneMatch = phoneValue.match(/(?:\+?\d[\d\s().-]{6,}\d)/) ||
    text.match(/(?:phone|電話番号|電話|携帯|tel|mobile|連絡先)[：:\s-]*((?:\+?\d[\d\s().-]{6,}\d))/i) ||
    text.match(/(?:\+81|0)\d[\d\s().-]{7,}\d/);
  if (phoneMatch) {
    parsed.phone = cleanValue(phoneMatch[1] || phoneMatch[0]).replace(/\s+/g, ' ');
    addRecognized(recognized, '電話番号');
  }

  const flightValue = matchLineValue(text, ['Flight Number', 'フライト情報', 'フライト', 'Flight', '航班']);
  const flightMatch = flightValue
    ? flightValue.match(/[A-Z]{2}\s*\d{1,4}[A-Z]?/i)
    : text.match(/(?:flight|フライト|航班)[^\n\r]*?([A-Z]{2}\s*\d{1,4}[A-Z]?)/i);
  if (flightMatch) {
    parsed.flight_number = (flightMatch[1] || flightMatch[0]).replace(/\s+/g, ' ').toUpperCase();
    addRecognized(recognized, 'フライト');
  }

  const route = parseRouteParts(text);
  const compactPickup = text.match(/(?:^|\n)\s*Pickup\s*[：:-]\s*([^\n\r]+)/i)?.[1] || '';
  const compactDropoff = text.match(/(?:^|\n)\s*Drop\s*[- ]?off\s*[：:-]\s*([^\n\r]+)/i)?.[1] || '';
  let pickupDetail = route.pickup || matchLineValue(text, ['Pick-Up Location', 'Pickup Location', 'Pick Up Location', '出発地', '乗車地', '乗車場所', '上車地', '上车地点', '上車地点', '上车地', '出发地点']) || cleanValue(compactPickup);
  let dropoffDetail = route.dropoff || matchLineValue(text, ['Drop-Off Location', 'Dropoff Location', 'Drop Off Location', '目的地', '降車地', '下車地', '下车地点', '下車地点', '下车地', '到达地点']) || cleanValue(compactDropoff);
  const addressValue = matchLineValue(text, ['住所', 'Address']);
  const googleMapsValue = matchLineValue(text, ['Google Maps', 'Google Map', 'Map']);
  const addressTarget = inferAddressDetailTarget(text);

  if (addressTarget === 'pickup') {
    pickupDetail = appendDetailParts(pickupDetail, [addressValue, googleMapsValue]);
  } else if (addressTarget === 'dropoff') {
    dropoffDetail = appendDetailParts(dropoffDetail, [addressValue, googleMapsValue]);
  }

  if (pickupDetail) {
    parsed.pickup_location_detail = pickupDetail;
    addRecognized(recognized, '乗車地詳細');
    const pickupArea = inferAreaCode(pickupDetail, areas);
    if (pickupArea) {
      parsed.pickup_area = pickupArea;
      addRecognized(recognized, '出発地区');
    }
  }

  if (dropoffDetail) {
    parsed.dropoff_location_detail = dropoffDetail;
    addRecognized(recognized, '目的地詳細');
    const finalArea = inferAreaCode(dropoffDetail, areas);
    const dropoffArea = inferFarthestRouteArea(route.parts || [], parsed.pickup_area, finalArea, areas) || finalArea;
    if (dropoffArea) {
      parsed.dropoff_area = dropoffArea;
      addRecognized(recognized, '目的地区域');
    }
  }

  if (!parsed.pickup_area) {
    const pickupArea = inferAreaCode(text, areas);
    if (pickupArea) {
      parsed.pickup_area = pickupArea;
      addRecognized(recognized, '出発地区');
    }
  }

  const vehicleValue = matchLineValue(text, ['車両', '车型', '車種', 'Vehicle', 'Car']);
  const vehicleType = parseVehicleType(vehicleValue || text);
  if (vehicleType) {
    parsed.vehicle_type = vehicleType;
    addRecognized(recognized, '車種');
  }

  const vehicleCountMatch = (vehicleValue || text).match(/(\d+)\s*台/);
  if (vehicleCountMatch && parseInt(vehicleCountMatch[1]) > 1) {
    parsed.vehicle_count_note = `${vehicleCountMatch[1]}台`;
    addRecognized(recognized, `車両台数(${vehicleCountMatch[1]}台)`);
  }

  const luggageValue = matchLineValue(text, ['お荷物', '荷物', 'Luggage', '行李']);
  const luggageText = luggageValue || text;
  const luggageValueSimple = (luggageValue || '').match(/^\s*(\d+)\s*$/);
  const luggageDetail = luggageText.match(/大型\s*(\d+)個?[、,\s]*小型\s*(\d+)個?/i);
  const luggageSmallOnly = luggageText.match(/小型\s*(\d+)個?/i);
  const luggageLargeOnly = luggageText.match(/大型\s*(\d+)個?/i);
  const luggageUnits = [...luggageText.matchAll(/(\d+)\s*(?:checked|check[- ]?in|carry[- ]?on|suitcases?|bags?|pieces?)/gi)];
  const luggageSimple = luggageText.match(/(?:荷物|luggage|行李)[：:\s]*(\d+)/i);
  if (luggageValueSimple) {
    parsed.luggage_count = parseInt(luggageValueSimple[1], 10);
    addRecognized(recognized, '荷物数');
  } else if (luggageDetail) {
    const large = parseInt(luggageDetail[1]) || 0;
    const small = parseInt(luggageDetail[2]) || 0;
    parsed.luggage_count = large + small;
    addRecognized(recognized, `荷物(${parsed.luggage_count})`);
  } else if (luggageSmallOnly || luggageLargeOnly) {
    parsed.luggage_count = parseInt((luggageSmallOnly || luggageLargeOnly)[1]) || 0;
    addRecognized(recognized, '荷物数');
  } else if (luggageUnits.length > 0) {
    parsed.luggage_count = luggageUnits.reduce((sum, match) => sum + (parseInt(match[1], 10) || 0), 0);
    addRecognized(recognized, `荷物(${parsed.luggage_count})`);
  } else if (luggageSimple) {
    parsed.luggage_count = parseInt(luggageSimple[1]);
    addRecognized(recognized, '荷物数');
  }

  const priceValue = matchLineValue(text, ['价格', '價格', '费用', '費用', '料金', 'Price']);
  const priceMatch = (priceValue || '').match(/(\d[\d,，\s]*)/);
  if (priceMatch) {
    parsed.manual_price = priceMatch[1].replace(/[^\d]/g, '');
    parsed.manual_price_reason = '粘贴文本价格';
    addRecognized(recognized, '手动价格');
  }

  const serviceValue = matchLineValue(text, ['服务内容', '服務內容', '服务', '服務', 'サービスタイプ', 'サービス内容', 'Service']);
  if (/半天|半日|half\s*day/i.test(serviceValue)) {
    parsed.service_type = 'charter';
    parsed.selected_hours = 5;
    addRecognized(recognized, '服务内容(半天游)');
  } else if (/一天|一日|整天|全天|full\s*day/i.test(serviceValue)) {
    parsed.service_type = 'charter';
    parsed.selected_hours = 10;
    addRecognized(recognized, '服务内容(一天游)');
  } else if (/单程|單程|片道|接送|送迎|transfer|point/i.test(serviceValue)) {
    parsed.service_type = 'transfer';
    addRecognized(recognized, '服务内容(单程接送)');
  }

  const hoursValue = matchLineValue(text, ['包车时间', '包車時間', '利用時間', 'Duration', 'Hours']);
  const hoursMatch = (hoursValue || text).match(/(\d+(?:\.\d+)?)\s*[- ]?\s*(?:時間|小时|hours?|h)/i);
  if (hoursMatch) {
    parsed.service_type = 'charter';
    parsed.selected_hours = parseFloat(hoursMatch[1]);
    addRecognized(recognized, `包车時間(${parsed.selected_hours}時間)`);
  } else {
    const endTime = parseTimeValue(matchLineValue(text, ['終了時間', '到着時間', '到着予定', '終了予定', '结束时间', 'End Time']));
    const selectedHours = calculateHoursFromTimes(parsed.pickup_time, endTime);
    if (selectedHours) {
      parsed.service_type = 'charter';
      parsed.selected_hours = selectedHours;
      addRecognized(recognized, `包车時間(${parsed.selected_hours}時間)`);
    }
  }

  if (!parsed.service_type) {
    if (/観光ツアー|チャーター|包车|包車|tour|charter/i.test(text)) {
      parsed.service_type = 'charter';
    } else {
      parsed.service_type = 'transfer';
    }
  }

  const tripDetails = [];
  if (parsed.vehicle_count_note) {
    tripDetails.push(`【車両台数：${parsed.vehicle_count_note}】`);
  }
  tripDetails.push(text);
  parsed.trip_details = tripDetails.join('\n');

  return { parsed, recognized };
}

export function parseAdminOrderBatchText(rawText, areas = []) {
  const defaultMonth = inferDefaultMonthFromBatch(rawText);
  return splitAdminOrderBatchText(rawText)
    .flatMap((block) => {
      const { parsed, recognized } = parseAdminOrderText(block, areas, { defaultMonth });
      const vehicleValue = matchLineValue(block, ['車両', '车型', '車種', 'Vehicle', 'Car']);
      const vehicleRequests = parseVehicleRequests(vehicleValue);
      if (vehicleRequests.length <= 1) {
        return [{ raw_text: block, parsed, recognized }];
      }
      return vehicleRequests.map((vehicleType, index) => ({
        raw_text: block,
        parsed: {
          ...parsed,
          vehicle_type: vehicleType,
        },
        recognized: [
          ...recognized,
          `車両分割(${index + 1}/${vehicleRequests.length})`,
        ],
      }));
    })
    .filter(({ parsed }) => {
      const fields = Object.keys(parsed || {}).filter((key) => key !== 'trip_details');
      return fields.length > 0;
    });
}
